import {FloatingIndicator} from "@mantine/core";
import { ArrowUp as IconArrowUp, ArrowUpRight as IconArrowUpRight, ArrowRight as IconArrowRight, ArrowDownRight as IconArrowDownRight, ArrowDown as IconArrowDown, ArrowDownLeft as IconArrowDownLeft, ArrowLeft as IconArrowLeft, ArrowUpLeft as IconArrowUpLeft, Compass as IconCompass, Circle as IconCircleDot } from "@phosphor-icons/react";
import {useMemo, useRef, useState, forwardRef} from "react";
import style from './DirectionInput.module.css';

export default function DirectionInput({value, onChange, disabled}) {

    const [rootRef, setRootRef] = useState(null);
    const controlsRefs = useRef({});
    const [, setTick] = useState(0);

    const setControlRef = useMemo(() => {
        const refs = {};
        return (direction) => {
            if (!refs[direction]) {
                refs[direction] = (node) => {
                    if (controlsRefs.current[direction] !== node) {
                        controlsRefs.current[direction] = node;
                        setTick((prev) => prev + 1);
                    }
                };
            }
            return refs[direction];
        };
    }, []);


    if(disabled) {
        value = '';
    }
    const prev = useRef(0);

    const rotate = useMemo(() => {
        let next = angles[value];
        let rotate = adjustAngle(prev.current, next);
        prev.current = rotate;
        return rotate;
    }, [value]);


    return (
        <div className={style.wrapper} ref={setRootRef}>
            <FloatingIndicator
                target={controlsRefs.current[value]}
                parent={rootRef}
                className={style.indicator}
            />
            <Tile direction="NW" value={value} onChange={onChange} disabled={disabled} ref={setControlRef('NW')}/>
            <Tile direction="N" value={value} onChange={onChange} disabled={disabled} ref={setControlRef('N')}/>
            <Tile direction="NE" value={value} onChange={onChange} disabled={disabled} ref={setControlRef('NE')}/>

            <Tile direction="W" value={value} onChange={onChange} disabled={disabled} ref={setControlRef('W')}/>
            <div className={disabled ? style.buttonFakeDisabled : style.buttonFake}>
                <div style={{transform: `rotate(${rotate}deg)`, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                    {disabled ? <IconCircleDot size={16}/> : <IconCompass size={16}/>}
                </div>
            </div>
            <Tile direction="E" value={value} onChange={onChange} disabled={disabled} ref={setControlRef('E')}/>

            <Tile direction="SW" value={value} onChange={onChange} disabled={disabled} ref={setControlRef('SW')}/>
            <Tile direction="S" value={value} onChange={onChange} disabled={disabled} ref={setControlRef('S')}/>
            <Tile direction="SE" value={value} onChange={onChange} disabled={disabled} ref={setControlRef('SE')}/>
        </div>
    )
}

const angles = {
    '': 0,
    NE: 0,
    N : -45,
    NW: -90,
    W: -135,
    SW: 180,
    S: 135,
    SE: 90,
    E: 45,
}


const icons = {
    N: IconArrowUp,
    NE: IconArrowUpRight,
    E: IconArrowRight,
    SE: IconArrowDownRight,
    S: IconArrowDown,
    SW: IconArrowDownLeft,
    W: IconArrowLeft,
    NW: IconArrowUpLeft,
    C: IconCompass
}

const Tile = forwardRef(function TileRaw({direction, value, onChange, disabled = false}, ref) {
    const IconComponent = icons[direction];
    const checked = direction === value;
    return (
        <div
            className={`${style['button-'+direction]} ${direction===value ? style.active : null}`}
            onClick={() => onChange(checked ? '' : direction)}
            disabled={disabled}
            title={direction}
            ref={ref}
        >
            <IconComponent size={16}/>
        </div>
    )
});


function normalizeAngle(angle) {
    // Normalize the angle to be within [0, 360) degrees
    return (angle % 360 + 360) % 360;
}

function adjustAngle(currentAngle, targetAngle) {
    let normalizedCurrent = normalizeAngle(currentAngle);
    let normalizedTarget = normalizeAngle(targetAngle);

    // Calculate the difference
    let delta = normalizedTarget - normalizedCurrent;

    //Adjust the target to ensure the shortest path
    if (delta > 180) {
        normalizedTarget -= 360;
    } else if (delta < -180) {
        normalizedTarget += 360;
    }


    return normalizedTarget;
}
